import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { brandConfig } from '../../config/brands';
import { manualProductTabLabel } from '../../config/brands/productLabels';
import { apiClient } from '../../services/apiClient';
import { useToast } from '../../hooks/useToast';
import { useMaterialsPanelStore } from '../../stores/materialsPanelStore';
import { formatDateTime as formatDate } from '../../utils/formatters';
import { renderMaterialPill } from './shared/MaterialCountPill';
import { SingleSelectDropdown } from '../../components/common/SingleSelectDropdown';
import { useBrandFilterOptions } from '../../features/products/useBrandFilterOptions';
import '../../components/common/CommonTable.css';
import '../../components/common/CommonFilters.css';
import './ProductsPageHeader.css';
import './ProductListPage.css';

/**
 * Amazon 商品 (`amazon_products` テーブル / `GET /amazon/products`)。
 *
 * NOTE: 手動登録商品の `features/products/types.ts::Product` とは別エンティティ。
 *       名前衝突で「同じ型の重複定義」に見えるため、ここでは AmazonProduct と明示する。
 */
interface AmazonProduct {
  id: string;
  asin: string;
  title?: string;
  brand_name?: string;
  brand_id?: string;
  manufacturer?: string;
  main_image_url?: string;
  price_amount?: number;
  price_currency?: string;
  marketplace_id?: string;
  marketplace_country?: string;
  created_at: string;
  last_fetched_at?: string;
  account_id?: string;
  cloud_material_count?: number;
  temp_material_count?: number;
  amazon_image_count?: number;
}

const SORT_STORAGE_KEY = 'products_sort_by';
const BRAND_FILTER_STORAGE_KEY = 'products_brand_filter';
const OWNERSHIP_FILTER_STORAGE_KEY = 'products_ownership_filter';

/** 出品の区別: 自社 = SKU あり (SP-API で画像を編集できる出品) / 他社 = SKU なし (カタログ参照のみ) */
type OwnershipFilter = '' | 'own' | 'other';
const OWNERSHIP_OPTIONS: { value: OwnershipFilter; label: string }[] = [
  { value: '', label: 'すべて' },
  { value: 'own', label: '自社' },
  { value: 'other', label: '他社' },
];

// ブランド名を和名と英名に分割する関数
const parseBrandName = (displayName: string | undefined): { japanese: string; english: string | null } => {
  if (!displayName) {
    return { japanese: '', english: null };
  }

  // "和名(英名)" 形式をパース
  const match = displayName.match(/^(.+?)\((.+?)\)$/);
  if (match) {
    return { japanese: match[1].trim(), english: match[2].trim() };
  }

  // パースできない場合はそのまま和名として扱う
  return { japanese: displayName, english: null };
};

const PRODUCTS_TAB_KEY = 'products_last_tab';

export default function ProductListPage() {
  const navigate = useNavigate();

  useEffect(() => {
    localStorage.setItem(PRODUCTS_TAB_KEY, '/products/amazon');
  }, []);
  const queryClient = useQueryClient();
  const toast = useToast();
  const openWithProductFilter = useMaterialsPanelStore((s) => s.openWithProductFilter);

  // LocalStorageから初期値を読み込む
  const [sortBy, setSortBy] = useState(() => {
    const saved = localStorage.getItem(SORT_STORAGE_KEY);
    return saved || 'last_fetched_at_desc';
  });

  // URL の brand_id があればそれを優先、無ければ LocalStorage から復元
  const [searchParams] = useSearchParams();
  const [brandFilter, setBrandFilter] = useState(() => {
    const fromUrl = searchParams.get('brand_id');
    if (fromUrl) return fromUrl;
    const saved = localStorage.getItem(BRAND_FILTER_STORAGE_KEY);
    return saved || '';
  });

  // URL の brand_id が後から変わったら追従
  useEffect(() => {
    const fromUrl = searchParams.get('brand_id');
    if (fromUrl && fromUrl !== brandFilter) {
      setBrandFilter(fromUrl);
    }
  }, [searchParams]);

  const [ownershipFilter, setOwnershipFilter] = useState<OwnershipFilter>(() => {
    const saved = localStorage.getItem(OWNERSHIP_FILTER_STORAGE_KEY);
    return saved === 'own' || saved === 'other' ? saved : '';
  });

  // sortByが変更されたらLocalStorageに保存
  useEffect(() => {
    localStorage.setItem(SORT_STORAGE_KEY, sortBy);
  }, [sortBy]);

  useEffect(() => {
    localStorage.setItem(OWNERSHIP_FILTER_STORAGE_KEY, ownershipFilter);
  }, [ownershipFilter]);

  // brandFilterが変更されたらLocalStorageに保存
  useEffect(() => {
    localStorage.setItem(BRAND_FILTER_STORAGE_KEY, brandFilter);
  }, [brandFilter]);

  // ソートヘッダークリック
  const parsedSort = sortBy.match(/^(.+)_(asc|desc)$/);
  const currentSortField = parsedSort ? parsedSort[1] : 'last_fetched_at';
  const currentSortOrder = parsedSort ? parsedSort[2] : 'desc';

  const handleSort = (field: string, defaultOrder: 'asc' | 'desc' = 'asc') => {
    if (currentSortField === field) {
      setSortBy(`${field}_${currentSortOrder === 'asc' ? 'desc' : 'asc'}`);
    } else {
      setSortBy(`${field}_${defaultOrder}`);
    }
  };

  const sortIndicator = (field: string) => {
    if (currentSortField !== field) return null;
    return <span className="sort-indicator">{currentSortOrder === 'asc' ? '▲' : '▼'}</span>;
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['products', sortBy, brandFilter, ownershipFilter],
    queryFn: async () => {
      const params: any = { sort: sortBy, limit: 50 };
      if (brandFilter) {
        params.brand_id = brandFilter;
      }
      if (ownershipFilter) {
        params.ownership = ownershipFilter;
      }
      const response = await apiClient.get('/amazon/products', { params });
      return response.data;
    },
  });

  // ブランドフィルタ選択肢 (Amazon商品が存在するブランドのみ・お気に入りを上位＋星アイコン)
  const brandOptions = useBrandFilterOptions('amazon');

  const products: AmazonProduct[] = data?.products || [];
  const total = data?.total || 0;

  const [refreshingAsins, setRefreshingAsins] = useState<Set<string>>(new Set());
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);

  const refreshProductMutation = useMutation({
    mutationFn: async ({ asin, accountId }: { asin: string; accountId?: string }) => {
      if (!accountId) {
        throw new Error('アカウント情報が見つかりません');
      }
      const response = await apiClient.post('/amazon/products/bulk', {
        asins: [asin],
        account_id: accountId
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      setRefreshingAsins(prev => {
        const next = new Set(prev);
        next.delete(variables.asin);
        return next;
      });
      if (data.success_count > 0) {
        toast.success('商品情報を更新しました');
      } else {
        toast.error('商品情報の更新に失敗しました');
      }
    },
    onError: (error: Error, variables) => {
      setRefreshingAsins(prev => {
        const next = new Set(prev);
        next.delete(variables.asin);
        return next;
      });
      toast.error(`更新に失敗しました: ${error.message}`);
    },
  });

  const handleRefreshProduct = (product: AmazonProduct, e: React.MouseEvent) => {
    e.stopPropagation();
    setRefreshingAsins(prev => new Set(prev).add(product.asin));
    refreshProductMutation.mutate({ asin: product.asin, accountId: product.account_id });
  };

  return (
    <div className="product-list-page">
      <header className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">
            <img src="/database_2.svg" alt="" className="page-title-icon page-title-icon--mobile-hidden" />
            {brandConfig.labels.productShort}一覧
          </h1>
        </div>
      </header>

      <nav className="products-page-tabs-bar">
        <div className="products-page-tabs">
          {(() => {
            const amazonTab = brandConfig.features.amazonIntegration && (
              <Link key="amazon" to="/products/amazon" className="products-tab active">
                <img src="/amazon_icon.svg" alt="" className="products-tab-icon" />
                <span>Amazon連携</span>
              </Link>
            );
            const manualTab = (
              <Link key="manual" to="/products/manual" className={`products-tab${brandConfig.features.amazonIntegration ? '' : ' active'}`}>
                <img src="/e_commerce_touch_buy.svg" alt="" className="products-tab-icon" />
                <span>{manualProductTabLabel(brandConfig)}</span>
              </Link>
            );
            return brandConfig.features.productsManualFirst
              ? [manualTab, amazonTab]
              : [amazonTab, manualTab];
          })()}
        </div>
        <p className="page-description products-tab-description">Amazon Selling Partner APIと連携して商品データを登録します</p>
      </nav>

      <section className="fetch-section">
        <div className="fetch-mode-buttons">
          <button
            type="button"
            className="btn-modal"
            onClick={() => navigate('/products/amazon-listings')}
          >
            <img src="/database_1.svg" alt="" style={{ width: '14px', height: '14px' }} />
            APIから追加
          </button>
          <button
            type="button"
            className="btn-modal"
            onClick={() => navigate('/products/asin-search')}
          >
            <img src="/shipment_search.svg" alt="" style={{ width: '14px', height: '14px' }} />
            ASINから追加
          </button>
        </div>
      </section>

      <section className="list-controls">
        {/* スマホ用トグルボタン */}
        <button
          type="button"
          className="filter-toggle-button"
          onClick={() => setIsFilterExpanded(!isFilterExpanded)}
        >
          <img src="/filter_sort_lines_descending.svg" alt="" className="filter-toggle-icon" />
          <span>フィルタ・並び替え</span>
          <span className="filter-toggle-count">全 {total} 件</span>
          <span className={`filter-toggle-chevron ${isFilterExpanded ? 'expanded' : ''}`} />
        </button>

        <div className={`controls-row ${isFilterExpanded ? 'expanded' : ''}`}>
          <div className="filter-section">
            <img src="/filter_sort_lines_descending.svg" alt="" className="filter-icon" />
            <span className="filter-label">フィルタ</span>
            <div className="filter-group">
              <label>ブランド</label>
              <SingleSelectDropdown
                label="すべて"
                labelOnly
                value={brandFilter}
                onChange={setBrandFilter}
                options={brandOptions}
                fitContent
              />
            </div>
            <div className="filter-group">
              <label>出品</label>
              <SingleSelectDropdown
                label="すべて"
                labelOnly
                value={ownershipFilter}
                onChange={(v) => setOwnershipFilter(v as OwnershipFilter)}
                options={OWNERSHIP_OPTIONS}
                fitContent
              />
            </div>
            <div className="total-count">
              全 <strong>{total}</strong> 件
            </div>
          </div>
          <div className="sort-section" />
        </div>
      </section>

      {isLoading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>読み込み中...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <p>商品データの取得に失敗しました</p>
        </div>
      ) : products.length === 0 ? (
        <div className="empty-state">
          {brandFilter || ownershipFilter ? (
            <p>条件に合う商品がありません。フィルタを変えてお試しください。</p>
          ) : (
            <p>商品データが登録されていません。上部のボタンから商品データを連携して追加してください。</p>
          )}
        </div>
      ) : (
        <div className="table-container">
          <table className="products-table common-table">
            <thead>
              <tr>
                <th className="col-image">画像</th>
                <th className="col-asin desktop-only">ASIN</th>
                <th className={`col-title desktop-only sortable-th${currentSortField === 'title' ? ' active-sort' : ''}`} onClick={() => handleSort('title', 'asc')}>商品名{sortIndicator('title')}</th>
                <th className="col-brand desktop-only">ブランド</th>
                <th className="col-marketplace desktop-only">マーケット</th>
                <th className="col-count desktop-only">商品画像数</th>
                <th className="col-count desktop-only">共有素材</th>
                <th className="col-count desktop-only">個人素材</th>
                <th className={`col-date desktop-only sortable-th${currentSortField === 'last_fetched_at' ? ' active-sort' : ''}`} onClick={() => handleSort('last_fetched_at', 'desc')}>最終取得日時{sortIndicator('last_fetched_at')}</th>
                <th className="col-mobile-info mobile-only">{brandConfig.labels.productShort}情報</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product) => (
                <tr
                  key={product.id}
                  className="clickable-row"
                  onClick={() => navigate(`/products/amazon/${product.asin}`)}
                >
                  <td className="col-image">
                    {product.main_image_url ? (
                      <img
                        src={product.main_image_url}
                        alt={product.title || 'Product'}
                        className="product-thumbnail"
                      />
                    ) : (
                      <div className="no-image">No Image</div>
                    )}
                  </td>
                  <td className="col-asin desktop-only">
                    <code>{product.asin}</code>
                  </td>
                  <td className="col-title desktop-only">
                    {product.title || '-'}
                  </td>
                  <td className="col-brand desktop-only">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      {(() => {
                        const brandDisplayName = product.brand_name || product.manufacturer;
                        const { japanese, english } = parseBrandName(brandDisplayName);

                        return (
                          <div className="brand-cell">
                            {product.brand_id && brandDisplayName ? (
                              <>
                                {english && (
                                  <span className="brand-english-name">
                                    {english}
                                  </span>
                                )}
                                <Link to={`/brands/${product.brand_id}`} className="brand-link" onClick={(e) => e.stopPropagation()}>
                                  {japanese}
                                </Link>
                              </>
                            ) : (
                              <span>{japanese || '-'}</span>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </td>
                  <td className="col-marketplace desktop-only">
                    {product.marketplace_country ? (
                      <div className="marketplace-cell">
                        <span className="marketplace-platform">Amazon</span>
                        <span className="marketplace-region">
                          {product.marketplace_country}
                        </span>
                      </div>
                    ) : (
                      <span>{product.marketplace_id || '-'}</span>
                    )}
                  </td>
                  <td className="col-count desktop-only">
                    <span className="image-count-value">{product.amazon_image_count ?? 0}</span>
                  </td>
                  <td className="col-count desktop-only">
                    {renderMaterialPill('shared', product.cloud_material_count ?? 0, () => openWithProductFilter(`amazon:${product.id}`, 'shared'))}
                  </td>
                  <td className="col-count desktop-only">
                    {renderMaterialPill('personal', product.temp_material_count ?? 0, () => openWithProductFilter(`amazon:${product.id}`, 'personal'))}
                  </td>
                  <td className="col-date desktop-only">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span>{formatDate(product.last_fetched_at || product.created_at)}</span>
                      <button
                        type="button"
                        onClick={(e) => handleRefreshProduct(product, e)}
                        disabled={refreshingAsins.has(product.asin)}
                        className="icon-button"
                        title="更新"
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: refreshingAsins.has(product.asin) ? 'not-allowed' : 'pointer',
                          padding: '0.25rem',
                          display: 'flex',
                          alignItems: 'center',
                          opacity: refreshingAsins.has(product.asin) ? 0.5 : 1,
                        }}
                      >
                        <img
                          src="/refresh.svg"
                          alt="更新"
                          style={{
                            width: '16px',
                            height: '16px',
                            animation: refreshingAsins.has(product.asin) ? 'spin 1s linear infinite' : 'none'
                          }}
                        />
                      </button>
                    </div>
                  </td>
                  {/* モバイル用：情報を縦積みで表示 */}
                  <td className="col-mobile-info mobile-only">
                    <div className="mobile-info-row1">
                      {(() => {
                        const brandDisplayName = product.brand_name || product.manufacturer;
                        const { japanese } = parseBrandName(brandDisplayName);
                        return (
                          <>
                            {product.brand_id && brandDisplayName ? (
                              <Link to={`/brands/${product.brand_id}`} className="brand-link" onClick={(e) => e.stopPropagation()}>
                                {japanese}
                              </Link>
                            ) : (
                              <span className="mobile-brand-name">{japanese || '-'}</span>
                            )}
                          </>
                        );
                      })()}
                      {product.marketplace_country && (
                        <span className="mobile-marketplace">{product.marketplace_country}</span>
                      )}
                      <code className="mobile-asin">{product.asin}</code>
                    </div>
                    <div className="mobile-info-title">
                      {product.title || '-'}
                    </div>
                    <div className="mobile-info-row3">
                      <span className="mobile-material-label">画像</span>
                      <span className="image-count-value">{product.amazon_image_count ?? 0}</span>
                      <span className="mobile-material-label">共有</span>
                      {renderMaterialPill('shared', product.cloud_material_count ?? 0, () => openWithProductFilter(`amazon:${product.id}`, 'shared'))}
                      <span className="mobile-material-label">個人</span>
                      {renderMaterialPill('personal', product.temp_material_count ?? 0, () => openWithProductFilter(`amazon:${product.id}`, 'personal'))}
                    </div>
                    <div className="mobile-info-row4">
                      <span className="mobile-date">{formatDate(product.last_fetched_at || product.created_at)}</span>
                      <button
                        type="button"
                        onClick={(e) => handleRefreshProduct(product, e)}
                        disabled={refreshingAsins.has(product.asin)}
                        className="icon-button"
                        title="更新"
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: refreshingAsins.has(product.asin) ? 'not-allowed' : 'pointer',
                          padding: '0.25rem',
                          display: 'flex',
                          alignItems: 'center',
                          opacity: refreshingAsins.has(product.asin) ? 0.5 : 1,
                        }}
                      >
                        <img
                          src="/refresh.svg"
                          alt="更新"
                          style={{
                            width: '16px',
                            height: '16px',
                            animation: refreshingAsins.has(product.asin) ? 'spin 1s linear infinite' : 'none'
                          }}
                        />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
